/* @jsx h */
import { defineComponent, h, ref } from "vue";
import { OrderItem } from "@lemmonhq/types";
import { useLocalization } from "@lemmonhq/uikit";
import { useAuth } from "../../../providers/Auth";

interface OrderCardItemsProps {
  items: OrderItem[];
  groupName?: string;
  allowAdditions?: boolean;
  quantityMode?: "add" | "reduce" | null;
  onQuantityChange?: (itemId: number, newQuantity: number) => void;
  modifiedQuantities?: Record<number, number>;
}

export const OrderCardItems = defineComponent<OrderCardItemsProps>({
  name: "OrderCardItems",
  props: [
    "items",
    "groupName",
    "allowAdditions",
    "quantityMode",
    "onQuantityChange",
    "modifiedQuantities",
  ],
  setup(props) {
    const { t } = useLocalization();
    const auth = useAuth();
    return () => {
      const { items, groupName } = props;

      if (!items || items.length === 0) {
        return null;
      }

      const getModifiedQuantity = (item: OrderItem): number => {
        return props.modifiedQuantities?.[item.id] ?? item.quantity;
      };

      const handleIncrement = (item: OrderItem) => {
        if (props.onQuantityChange) {
          const currentQty = getModifiedQuantity(item);
          props.onQuantityChange(item.id, currentQty + 1);
        }
      };

      const handleDecrement = (item: OrderItem) => {
        if (props.onQuantityChange) {
          const currentQty = getModifiedQuantity(item);
          if (!props.quantityMode && currentQty > 0) {
            props.onQuantityChange(item.id, currentQty - 1);
          }
          if (props.quantityMode === "add" && currentQty > item.quantity) {
            props.onQuantityChange(item.id, currentQty - 1);
          }
          if (props.quantityMode === "reduce" && currentQty > 0) {
            props.onQuantityChange(item.id, currentQty - 1);
          }
        }
      };

      return (
        <div class=" space-y-2 rounded">
          {groupName ? (
            <div class="rounded font-semibold text-accent">
              {groupName.toUpperCase()}
            </div>
          ) : null}

          <div class="space-y-3">
            {items.map((item) => (
              <div key={item.id} class="flex items-start gap-3">
                <div class="bg-[var(--order-card-quantity-background)] text-[var(--order-card-quantity-content)] rounded px-2 flex items-center justify-center flex-shrink-0">
                  {item.quantity}
                </div>
                <div class="flex-1">
                  <div class="text-accent">{item.product.name}</div>
                  {item.notes && (
                    <div class="text-xs">
                      {t("orders.card.note")} {item.notes}
                    </div>
                  )}
                  {item.bundles && item.bundles.length > 0 && (
                    <div class="text-[var(--order-card-muted-content)]">
                      {item.bundles.map((bundle, index) => (
                        <div key={index}>
                          {bundle.extra?.title}
                          {bundle.price > 0 &&
                            ` (+${bundle.price.toFixed(2)} ${auth.user.value?.restaurant.currency || "CHF"})`}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {props.allowAdditions && (
                  <div class="flex items-center gap-1.5 ml-2">
                    <button
                      onClick={() => handleDecrement(item)}
                      class="p-0 btn btn-sm btn-neutral w-6 h-6 min-h-0 flex justify-center items-center"
                      disabled={
                        !props.quantityMode
                          ? getModifiedQuantity(item) <= 0
                          : props.quantityMode === "add"
                            ? getModifiedQuantity(item) <= item.quantity
                            : getModifiedQuantity(item) <= 0
                      }
                    >
                      <span class="material-symbols-outlined text-md">
                        remove
                      </span>
                    </button>
                    <span class="text-xs font-medium min-w-4 text-center">
                      {getModifiedQuantity(item)}
                    </span>
                    <button
                      onClick={() => handleIncrement(item)}
                      class="p-0 btn btn-sm btn-neutral w-6 h-6 min-h-0 flex justify-center items-center"
                      disabled={
                        props.quantityMode === "reduce"
                          ? getModifiedQuantity(item) >= item.quantity
                          : false
                      }
                    >
                      <span class="material-symbols-outlined text-md">add</span>
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    };
  },
});
